import React, {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-final-form';
import {useDispatch, useSelector} from 'react-redux';
import {getUser, logout} from '@core/actions/UserActions';
import SearchableSelect from '../../../common/SearchableSelect';
import PhoneCodeSelect from '../../../common/PhoneCodeSelect';
import {showOverlay, hideOverlay} from '../../../common/Overlay';
import ResetPasswordPage from '@auth/components/ResetPasswordPage';

export default function PersonalInfo({
    countries,
    errors,
    isGuest,
    isNewCustomerRef,
    onClearErrors,
    onForgotPassword,
    showResetPassword = false,
    resetPasswordEmail = '',
    onResetPasswordBack,
}) {
    const [isNewCustomer, setIsNewCustomer] = useState(true);
    const form = useForm();
    const values = form.getState().values;
    const dispatch = useDispatch();

    isNewCustomerRef.current = isNewCustomer;

    const {user} = useSelector(state => state.user);

    const handleChangeAccount = () => {
        showOverlay();
        dispatch(logout())
            .then(() => dispatch(getUser()))
            .finally(() => hideOverlay());
    };

    const countryOptions = useMemo(
        () => Object.values(countries || {})
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(c => ({value: c.code, label: c.name, phoneCode: String(c.phoneCode), raw: c})),
        [countries]
    );


    if (!isGuest) {
        return <div className="resume-form__personal">
            <div className="resume-form__personal-inner">
                <div className="resume-form__account-box">
                    <div className="resume-form__account-info">
                        <div className="resume-form__account-avatar">
                            {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="resume-form__account-details">
                            {user?.name && <div className="resume-form__account-name">{user.name}</div>}
                            <div className="resume-form__account-email">{user?.email || ''}</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="resume-form__account-switch"
                        onClick={handleChangeAccount}
                    >
                        Not you? Switch account
                    </button>
                </div>
            </div>
        </div>;
    }

    const change = (field, val) => form.change(field, val);

    const selectCountry = (code) => {
        change('country', code);
        const c = Object.values(countries).find(item => item.code === code);
        if (c) {
            change('phoneCode', '+' + c.phoneCode);
            change('countryCodeFlag', c.code);
        }
        onClearErrors && onClearErrors('country');
        onClearErrors && onClearErrors('phoneCode');
    };

    const selectPhoneCode = (code) => {
        const c = Object.values(countries).find(item => item.code === code);
        if (c) {
            change('phoneCode', '+' + c.phoneCode);
            change('country', c.code);
            change('countryCodeFlag', c.code);
        }
        onClearErrors && onClearErrors('phoneCode');
        onClearErrors && onClearErrors('country');
    };

    const clearCountry = () => {
        change('country', '');
        change('countryCodeFlag', '');
    };

    return <div className="resume-form__personal">
        <div className="resume-form__personal-inner">
            <div className="resume-form__auth-toggle">
                <div className="resume-form__auth-option">
                        <button type="button"
                                className={`btn btn-default ${isNewCustomer ? 'active' : ''}`}
                                onClick={() => { setIsNewCustomer(true); onClearErrors && onClearErrors(); }}>
                            NEW CUSTOMER
                        </button>
                </div>
                <div className="resume-form__auth-option">
                        <button type="button"
                                className={`btn btn-default text-overflow-cut ${!isNewCustomer ? 'active' : ''}`}
                                onClick={() => { setIsNewCustomer(false); onClearErrors && onClearErrors(); }}>
                            RETURNING CUSTOMER
                        </button>
                </div>
            </div>

            {!isNewCustomer && !showResetPassword && (
                <div className="resume-form__personal-grid">
                    <div className="resume-form__personal-column">
                        <div className="resume-form__field">
                            <label className="resume-form__field-label control-label">Email</label>
                            <div className={`resume-form__field-control ${errors.email ? 'error' : ''}`}>
                                <input type="text" className="form-control"
                                       value={values.email || ''}
                                       onChange={e => { change('email', e.target.value); onClearErrors && onClearErrors('email'); }} />
                                {errors.email && <p className="field-error">{errors.email}</p>}
                            </div>
                        </div>
                    </div>
                    <div className="resume-form__personal-column">
                        <div className="resume-form__field">
                            <label className="resume-form__field-label control-label">Password</label>
                            <div className={`resume-form__field-control ${errors.password ? 'error' : ''}`}>
                                <input type="password" className="form-control"
                                       value={values.password || ''}
                                       onChange={e => { change('password', e.target.value); onClearErrors && onClearErrors('password'); }} />
                                {errors.password && <p className="field-error">{errors.password}</p>}
                                <button
                                    type="button"
                                    className="resume-form__forgot-password"
                                    onClick={() => onForgotPassword && onForgotPassword(values.email || '')}
                                >
                                    Forgot password?
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!isNewCustomer && showResetPassword && (
                <div className="resume-form__reset-password-inline">
                    <ResetPasswordPage
                        initialEmail={resetPasswordEmail}
                        onSignIn={onResetPasswordBack}
                    />
                </div>
            )}

            {isNewCustomer && (
                <div className="resume-form__personal-grid">
                    <div className="resume-form__personal-column">
                        <div className="resume-form__field">
                            <label className="resume-form__field-label control-label">Name</label>
                            <div className={`resume-form__field-control ${errors.name ? 'error' : ''}`}>
                                <input type="text" className="form-control"
                                       value={values.name || ''}
                                       onChange={e => { change('name', e.target.value); onClearErrors && onClearErrors('name'); }} />
                                {errors.name && <p className="field-error">{errors.name}</p>}
                            </div>
                        </div>
                        <div className="resume-form__field">
                            <label className="resume-form__field-label control-label">E-mail</label>
                            <div className={`resume-form__field-control ${errors.email ? 'error' : ''}`}>
                                <input type="text" className="form-control"
                                       value={values.email || ''}
                                       onChange={e => { change('email', e.target.value); onClearErrors && onClearErrors('email'); }} />
                                {errors.email && <p className="field-error">{errors.email}</p>}
                            </div>
                        </div>
                    </div>
                    <div className="resume-form__personal-column">
                        <div className="resume-form__field">
                            <label className="resume-form__field-label control-label">Country</label>
                            <div className={`resume-form__field-control resume-form__country ${errors.country ? 'error' : ''}`}>
                                <SearchableSelect
                                    options={countryOptions}
                                    value={values.country || ''}
                                    placeholder="Select country"
                                    error={errors.country}
                                    onSelect={selectCountry}
                                    onClear={clearCountry}
                                    getLabel={o => o.label}
                                />
                                {errors.country && <p className="field-error">{errors.country}</p>}
                            </div>
                        </div>
                        <div className="resume-form__field">
                            <label className="resume-form__field-label control-label">Contact number</label>
                            <div className="resume-form__field-control phone-group">
                                <div className="resume-form__phone-row">
                                    <div className={`resume-form__phone-code ${errors.phoneCode ? 'error' : ''}`}>
                                        <PhoneCodeSelect
                                            countries={countries}
                                            value={values.countryCodeFlag || ''}
                                            onSelect={selectPhoneCode}
                                            error={errors.phoneCode}
                                        />
                                    </div>
                                    <div className={`resume-form__phone-number ${errors.phoneNumber ? 'error' : ''}`}>
                                        <input type="text" className="form-control"
                                               maxLength={14}
                                               placeholder="Phone number"
                                               value={values.phoneNumber || ''}
                                               onChange={e => { change('phoneNumber', e.target.value.replace(/[^0-9]/g, '')); onClearErrors && onClearErrors('phoneNumber'); onClearErrors && onClearErrors('phoneCode'); }} />
                                        {(errors.phoneCode || errors.phoneNumber) && (
                                            <p className="field-error">{errors.phoneCode || errors.phoneNumber}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        <div className="clearfix"></div>
    </div>;
}
